import React, { useState } from "react";
import QRCode from "qrcode";
import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Button,
  Input,
  Card,
  Select,
  Typography,
  Row,
  Col,
  Space,
  Image,
  Divider,
  InputNumber,
  message,
  Spin,
} from "antd";
import {
  QrcodeOutlined,
  DownloadOutlined,
  FileImageOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;
const { Option } = Select;

export default function Qr() {
  const [qrCodes, setQrCodes] = useState([]); // QR codes générés
  const [count, setCount] = useState(1); // Nombre de QR codes
  const [pdfFile, setPdfFile] = useState("/a5_white.pdf"); // Fichier PDF par défaut
  const [loading, setLoading] = useState(false); // État de chargement
  const [generating, setGenerating] = useState(false); // État de génération

  // Options pour les fichiers PDF
  const pdfOptions = [
    { value: "/card.pdf", label: "PVC  original - modèle 4" },
  ];

  // Fonction pour charger le fichier PDF de base
  const loadBasePdf = async () => {
    try {
      const response = await fetch(pdfFile);
      if (!response.ok) {
        throw new Error("Impossible de charger le fichier PDF");
      }
      const basePdfBytes = await response.arrayBuffer();
      return basePdfBytes;
    } catch (error) {
      message.error("Erreur lors du chargement du PDF : " + error.message);
      throw error;
    }
  };

  // Fonction pour générer une chaîne aléatoire unique
  const generateRandomData = () => {
    const randomNumber = Math.floor(Math.random() * 1_000_000_000);
    return `https://aiglepay.fintech-aigle.com/#/${randomNumber}`;
  };

  // Générer plusieurs QR codes
  const generateQRCodes = async () => {
    if (count < 1 || count > 500) {
      message.warning("Le nombre de QR codes doit être entre 1 et 500");
      return;
    }

    setGenerating(true);

    try {
      const newQrCodes = [];

      for (let i = 0; i < count; i++) {
        const randomData = generateRandomData();
        const qrCode = await QRCode.toDataURL(randomData, {
          width: 200,
          margin: 2,
        });
        newQrCodes.push({ data: randomData, qrCode });
      }

      setQrCodes(newQrCodes);
      message.success(`${count} QR codes générés avec succès !`);
    } catch (error) {
      console.error("Erreur lors de la génération des QR codes :", error);
      message.error("Erreur lors de la génération des QR codes");
    } finally {
      setGenerating(false);
    }
  };

  // Fonction pour ajouter un QR code au centre d'une page PDF
  const generatePdfWithQrCode = async (qrCodeBase64) => {
    const basePdfBytes = await loadBasePdf();
    const pdfDoc = await PDFDocument.load(basePdfBytes);

    const qrImageBytes = await fetch(qrCodeBase64).then((res) =>
      res.arrayBuffer()
    );
    const qrImage = await pdfDoc.embedPng(qrImageBytes);

    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    const { width, height } = firstPage.getSize();

    // Taille du QR code : 60% de la page
    const qrSize = Math.min(width, height) * 0.6;

    const x = (width - qrSize) / 2; // Centrage horizontal
    const y = (height - qrSize) / 2; // Centrage vertical

    firstPage.drawImage(qrImage, {
      x,
      y,
      width: qrSize,
      height: qrSize,
    });

    return await pdfDoc.save();
  };

  // Télécharger les PDF avec QR codes dans un fichier ZIP
  const downloadAsZip = async () => {
    if (qrCodes.length === 0) {
      message.warning(
        "Aucun QR code à télécharger. Générez d'abord des QR codes."
      );
      return;
    }

    setLoading(true);

    try {
      const zip = new JSZip();

      for (let i = 0; i < qrCodes.length; i++) {
        const { data, qrCode } = qrCodes[i];
        const pdfBytes = await generatePdfWithQrCode(qrCode);

        // Ajout du PDF au ZIP
        zip.file(`${data}.pdf`, pdfBytes);

        // Mise à jour du message de progression
        if (i % 10 === 0) {
          message.loading(`Génération en cours... ${i + 1}/${qrCodes.length}`);
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "qrcodes_with_pdfs.zip");
      message.success("Téléchargement terminé avec succès !");
    } catch (error) {
      console.error("Erreur lors de la génération du ZIP :", error);
      message.error("Erreur lors de la génération du fichier ZIP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={2} style={{ textAlign: "center", marginBottom: "32px" }}>
          <QrcodeOutlined style={{ marginRight: "8px" }} />
          Générateur de QR Codes
        </Title>

        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card type="inner" title="Configuration">
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                <div>
                  <Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    Choisissez un modèle PDF :
                  </Text>
                  <Select
                    value={pdfFile}
                    onChange={setPdfFile}
                    style={{ width: "100%", maxWidth: "400px" }}
                    placeholder="Sélectionnez un modèle"
                  >
                    {pdfOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <Text
                    strong
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    Nombre de QR Codes à générer (max : 500) :
                  </Text>
                  <Row gutter={16} align="middle">
                    <Col>
                      <InputNumber
                        min={1}
                        max={500}
                        value={count}
                        onChange={(value) => setCount(value || 1)}
                        style={{ width: "120px" }}
                      />
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        icon={<QrcodeOutlined />}
                        onClick={generateQRCodes}
                        loading={generating}
                        size="large"
                      >
                        Générer les QR Codes
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Space>
            </Card>
          </Col>

          {qrCodes.length > 0 && (
            <Col span={24}>
              <Card
                type="inner"
                title={`QR Codes générés (${qrCodes.length})`}
                extra={
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={downloadAsZip}
                    loading={loading}
                    size="large"
                  >
                    Télécharger les PDF (ZIP)
                  </Button>
                }
              >
                <Row gutter={[16, 16]}>
                  {qrCodes.map((qr, index) => (
                    <Col key={index} xs={12} sm={8} md={6} lg={4} xl={3}>
                      <Card
                        size="small"
                        hoverable
                        cover={
                          <div style={{ padding: "8px", textAlign: "center" }}>
                            <Image
                              src={qr.qrCode}
                              alt={`QR Code ${index + 1}`}
                              width={100}
                              height={100}
                              preview={{
                                mask: (
                                  <FileImageOutlined
                                    style={{ fontSize: "20px" }}
                                  />
                                ),
                              }}
                            />
                          </div>
                        }
                      >
                        <Card.Meta
                          title={
                            <Text ellipsis style={{ fontSize: "12px" }}>
                              {qr.data}
                            </Text>
                          }
                          description={`QR #${index + 1}`}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Divider />

                <div style={{ textAlign: "center" }}>
                  <Space>
                    <Text type="secondary">
                      {qrCodes.length} QR code{qrCodes.length > 1 ? "s" : ""}{" "}
                      généré{qrCodes.length > 1 ? "s" : ""}
                    </Text>
                    <Divider type="vertical" />
                    <Text type="secondary">
                      Modèle sélectionné :{" "}
                      {pdfOptions.find((opt) => opt.value === pdfFile)?.label}
                    </Text>
                  </Space>
                </div>
              </Card>
            </Col>
          )}
        </Row>

        {(loading || generating) && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <Card style={{ textAlign: "center", minWidth: "300px" }}>
              <Spin size="large" />
              <div style={{ marginTop: "16px" }}>
                <Text strong>
                  {generating
                    ? "Génération des QR codes..."
                    : "Création du fichier ZIP..."}
                </Text>
              </div>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
}
